import React, { memo } from "react";
import { Divider } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { Badge } from "../preview/Badge";
import { FeeSummary } from "./FeeSummary";
import { ParticipantFullSummary } from "../../types/participant.types";
import { RegistrationRequest } from "../../types/registration.types";
import { ConferenceConfiguration } from "../../types/conference.types";
import {
  Fee,
  FeeType,
  ParticipantFeeSummary,
} from "../../types/fee.types";
import { NO_VALUE } from "../../utils/constants";
import type { ParticipantSummaryChanges } from "./participantSummarySectionTypes";

export interface ParticipantSummaryAttendanceFeeSectionProps {
  participant: ParticipantFullSummary;
  modifiedData: RegistrationRequest | null | undefined;
  displayConfigJson: ConferenceConfiguration;
  changes: ParticipantSummaryChanges;
  allParticipantFees: Fee[];
}

const emptyDefaultFeeSummary = {
  name: NO_VALUE,
  feeChoiceId: 0,
  participantId: 0,
  organizingPrice: 0,
  lunchPrice: 0,
  totalPrice: 0,
  feeType: FeeType.DEFAULT,
  numberOfAttendants: 0,
  attendantsName: "",
  participantFeeId: 0,
} satisfies ParticipantFeeSummary;

const ParticipantSummaryAttendanceFeeSectionInner: React.FC<
  ParticipantSummaryAttendanceFeeSectionProps
> = ({
  participant,
  modifiedData,
  displayConfigJson,
  changes,
  allParticipantFees,
}) => {
  const hasModifiedParticipantFeeData = !!(
    modifiedData?.participantFee?.participationFeeId != null &&
    modifiedData.participantFee.participationFeeId !== 0
  );
  const showParticipantFeeSection =
    participant.participationFee ||
    (!!modifiedData &&
      displayConfigJson.participantFeeEnabled &&
      hasModifiedParticipantFeeData);

  if (!showParticipantFeeSection) return null;

  let feeBody: React.ReactNode = null;
  if (
    changes["participantFee"] !== undefined &&
    changes["participantFee"].modified &&
    modifiedData?.participantFee
  ) {
    const modifiedFee = allParticipantFees.find(
      (f) => f.id === modifiedData.participantFee?.participationFeeId,
    );

    if (modifiedFee) {
      const modifiedFeeSummary: ParticipantFeeSummary = {
        feeChoiceId: participant.participationFee?.feeChoiceId || 0,
        participantId: participant.id,
        name: modifiedFee.name,
        organizingPrice: modifiedFee.price,
        lunchPrice: modifiedFee.lunchPrice,
        totalPrice: modifiedFee.totalPrice,
        feeType: modifiedFee.feeType,
        numberOfAttendants: 0,
        attendantsName: "",
        participantFeeId: participant.participationFee?.participantFeeId || 0,
      };
      feeBody = (
        <FeeSummary
          feeSummary={modifiedFeeSummary}
          originalFeeSummary={
            participant?.participationFee ?? emptyDefaultFeeSummary
          }
        />
      );
    }
  } else if (participant.participationFee) {
    feeBody = <FeeSummary feeSummary={participant.participationFee} />;
  }

  return (
    <>
      <Badge color="red">
        <Trans>Attendance Fee</Trans>
      </Badge>
      {feeBody}
      <Divider />
    </>
  );
};

export const ParticipantSummaryAttendanceFeeSection = memo(
  ParticipantSummaryAttendanceFeeSectionInner,
);
